// Interactive guided tour. Spotlights real UI in sequence and, on action
// steps, waits for the user to actually do the thing (pick an instrument, tap a
// pad, record a loop), praises them, then advances. By the end they've made a
// loop. The overlay can't be dismissed by clicking away — only via Skip.

export interface TourAction {
  /** Short instruction shown under the body, e.g. "Tap any pad". */
  cue: string;
  /** Praise shown when they do it, e.g. "Nice!". */
  success: string;
  /** Complete when this event fires on an element matching the selector. */
  on?: { selector: string; event: string };
  /** Complete when an element matching this selector is added to the DOM. */
  appear?: string;
}

export interface TourStep {
  /** CSS selector of the element to spotlight. Omit for a centered card. */
  target?: string;
  title: string;
  body: string;
  /** If set, this is an action step: the user must do it to advance. */
  action?: TourAction;
}

export interface Tour {
  element: HTMLElement;
  start(): void;
  stop(): void;
}

const PAD = 8;

export function createTour(steps: readonly TourStep[]): Tour {
  const overlay = document.createElement("div");
  overlay.className = "tour";
  overlay.hidden = true;

  // Blocks interaction on info steps; switched off on action steps so the user
  // can click the spotlighted control.
  const blocker = document.createElement("div");
  blocker.className = "tour__blocker";

  const spot = document.createElement("div");
  spot.className = "tour__spot";

  const card = document.createElement("div");
  card.className = "tour__card";
  card.setAttribute("role", "dialog");
  card.setAttribute("aria-modal", "true");
  const counter = document.createElement("div");
  counter.className = "tour__counter";
  const title = document.createElement("h2");
  title.className = "tour__title";
  const body = document.createElement("p");
  body.className = "tour__body";
  const cue = document.createElement("div");
  cue.className = "tour__cue";
  const controls = document.createElement("div");
  controls.className = "tour__controls";
  const skip = btn("Skip tour", "btn btn--ghost btn--sm");
  const back = btn("Back", "btn btn--ghost btn--sm");
  const next = btn("Next", "btn btn--sm");
  controls.append(skip, back, next);
  card.append(counter, title, body, cue, controls);

  overlay.append(blocker, spot, card);

  let i = 0;
  let cleanup: (() => void) | null = null;
  let advanceTimer = 0;

  const clearArmed = (): void => {
    if (cleanup) {
      cleanup();
      cleanup = null;
    }
    if (advanceTimer) {
      window.clearTimeout(advanceTimer);
      advanceTimer = 0;
    }
  };

  const place = (): void => {
    const step = steps[i];
    if (!step) return;
    const el = step.target ? document.querySelector(step.target) : null;
    if (el) {
      const r = el.getBoundingClientRect();
      spot.style.left = `${r.left - PAD}px`;
      spot.style.top = `${r.top - PAD}px`;
      spot.style.width = `${r.width + PAD * 2}px`;
      spot.style.height = `${r.height + PAD * 2}px`;
      positionCard(card, r);
    } else {
      spot.style.left = "50%";
      spot.style.top = "50%";
      spot.style.width = "0px";
      spot.style.height = "0px";
      card.style.left = "50%";
      card.style.top = "50%";
      card.style.transform = "translate(-50%, -50%)";
    }
  };

  const goTo = (n: number): void => {
    clearArmed();
    i = Math.max(0, Math.min(steps.length - 1, n));
    render();
  };

  const complete = (): void => {
    const step = steps[i];
    if (!step?.action) return;
    clearArmed();
    cue.className = "tour__cue is-done";
    cue.textContent = `✓  ${step.action.success}`;
    next.hidden = false;
    next.textContent = i === steps.length - 1 ? "Done" : "Next";
    // Auto-advance shortly after the praise so it feels responsive.
    advanceTimer = window.setTimeout(() => {
      if (i === steps.length - 1) stop();
      else goTo(i + 1);
    }, 1100);
  };

  const arm = (action: TourAction): void => {
    if (action.on) {
      const ev = action.on.event;
      const sel = action.on.selector;
      const handler = (e: Event): void => {
        const t = e.target as HTMLElement | null;
        if (t && t.closest(sel)) complete();
      };
      document.addEventListener(ev, handler, true);
      cleanup = () => document.removeEventListener(ev, handler, true);
    } else if (action.appear) {
      const sel = action.appear;
      const obs = new MutationObserver((muts) => {
        for (const m of muts) {
          for (const node of m.addedNodes) {
            if (node.nodeType !== 1) continue;
            const el = node as Element;
            if (el.matches?.(sel) || el.querySelector?.(sel)) {
              complete();
              return;
            }
          }
        }
      });
      obs.observe(document.body, { childList: true, subtree: true });
      cleanup = () => obs.disconnect();
    }
  };

  function render(): void {
    const step = steps[i];
    if (!step) return;
    counter.textContent = `${i + 1} / ${steps.length}`;
    title.textContent = step.title;
    body.textContent = step.body;
    back.disabled = i === 0;

    if (step.action) {
      // Action step: let the user interact, wait for them to do it.
      overlay.classList.add("is-interactive");
      cue.className = "tour__cue";
      cue.textContent = step.action.cue;
      cue.hidden = false;
      next.hidden = true; // appears after they complete the action
      arm(step.action);
    } else {
      overlay.classList.remove("is-interactive");
      cue.hidden = true;
      next.hidden = false;
      next.textContent = i === steps.length - 1 ? "Done" : "Next";
    }
    place();
  }

  const stop = (): void => {
    clearArmed();
    overlay.hidden = true;
    window.removeEventListener("resize", place);
    window.removeEventListener("scroll", place, true);
  };

  next.addEventListener("click", () => {
    if (i < steps.length - 1) goTo(i + 1);
    else stop();
  });
  back.addEventListener("click", () => goTo(i - 1));
  skip.addEventListener("click", stop);
  // NOTE: deliberately no click-to-dismiss on the overlay/blocker.

  return {
    element: overlay,
    start() {
      clearArmed();
      i = 0;
      overlay.hidden = false;
      window.addEventListener("resize", place);
      window.addEventListener("scroll", place, true);
      render();
    },
    stop,
  };
}

/** Place the card just outside the target rect, clamped to the viewport. */
function positionCard(card: HTMLElement, r: DOMRect): void {
  card.style.transform = "none";
  const cw = 300;
  const ch = card.offsetHeight || 180;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let left = r.left;
  let top = r.bottom + 14;
  if (top + ch > vh - 12) top = r.top - ch - 14;
  if (top < 12) top = Math.min(vh - ch - 12, r.bottom + 14);
  left = Math.max(12, Math.min(vw - cw - 12, left));
  card.style.left = `${left}px`;
  card.style.top = `${Math.max(12, top)}px`;
}

function btn(text: string, className: string): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = className;
  b.textContent = text;
  return b;
}
